import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Timer, ChevronRight } from "lucide-react";
import { useVehicleSessions } from "@/hooks/useSessions";

interface VehicleSessionsSectionProps {
  vehicleId: string;
  className?: string;
}

export function VehicleSessionsSection({
  vehicleId,
  className = "",
}: VehicleSessionsSectionProps) {
  const { t } = useTranslation(["pages", "common"]);
  const { data, isLoading, error } = useVehicleSessions(vehicleId, 3);
  const sessions = data ?? [];

  return (
    <div
      className={`bg-white rounded-lg border border-secondary-200 ${className}`}
    >
      <div className="p-6 border-b border-secondary-100">
        <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
          <Timer className="h-5 w-5 text-primary-500" />
          {t("pages:vehicles.sessionsSection.title")}
        </h2>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div
            className="animate-pulse space-y-2"
            data-testid="sessions-loading"
          >
            <div className="h-10 bg-secondary-200 rounded"></div>
            <div className="h-10 bg-secondary-200 rounded"></div>
            <div className="h-10 bg-secondary-200 rounded"></div>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600" role="alert">
            {t("pages:vehicles.sessionsSection.noSessions")}
          </p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-secondary-500">
            {t("pages:vehicles.sessionsSection.noSessions")}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {sessions.map((session) => (
                <Link
                  key={session.id}
                  to={`/sessions/${session.id}`}
                  className="flex items-center justify-between p-3 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-secondary-900 truncate">
                      {session.title}
                    </div>
                    <div className="text-xs text-secondary-500">
                      {new Date(session.conductedAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        },
                      )}
                      {" · "}
                      {session.event.track.name}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-secondary-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
            <div className="pt-4 mt-4 border-t border-secondary-100">
              <Link
                to={`/sessions?vehicleId=${vehicleId}`}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t("pages:vehicles.sessionsSection.seeMore")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
